/* eslint-disable prefer-destructuring */
import { ONBOARDING_USER_TYPE, resolveOnboardingUserId } from './onboardingLinkParams';

function trim(value) {
  return value == null ? '' : String(value).trim();
}

function findOptionLabel(options, value) {
  if (!value || !Array.isArray(options)) return null;
  const match = options.find((option) => option.value === value);
  return match ? match.label : null;
}

function setIfPresent(target, key, value) {
  const normalized = trim(value);
  if (normalized) {
    target[key] = normalized;
  }
}

function resolveUserType(registration, options = {}) {
  return registration.userType ?? options.userType ?? null;
}

/** Broker onboarding only — all other paths must send asBroker: false. */
export function resolveAsBroker(registration, options = {}) {
  return resolveUserType(registration, options) === ONBOARDING_USER_TYPE.BROKER;
}

function buildAccountFields(account = {}) {
  return {
    email: trim(account.email),
    password: account.password || '',
    firstName: trim(account.firstName),
    lastName: trim(account.lastName),
  };
}

function buildOrganisationFields(organisation = {}, options = {}) {
  const { industries = [], regulatoryBodies = [] } = options;
  const fields = {};

  setIfPresent(fields, 'fullBusinessName', organisation.businessName);
  setIfPresent(fields, 'tradingName', organisation.tradingName);
  setIfPresent(fields, 'registrationNumber', organisation.registrationNumber);
  setIfPresent(fields, 'businessRegistrationNumber', organisation.registrationNumber);

  const industryCode = trim(organisation.industry);
  if (industryCode) {
    fields.industryCode = industryCode;
    const industryName = findOptionLabel(industries, industryCode);
    if (industryName) fields.industryName = industryName;
  }

  const regulatoryBodyCode = trim(organisation.regulatoryBody);
  if (regulatoryBodyCode) {
    fields.regulatoryBodyCode = regulatoryBodyCode;
    const regulatoryBodyName = findOptionLabel(regulatoryBodies, regulatoryBodyCode);
    if (regulatoryBodyName) fields.regulatoryBodyName = regulatoryBodyName;
  }

  return fields;
}

function buildPlanFields(plan = {}) {
  const fields = {};
  setIfPresent(fields, 'planName', plan.name);
  setIfPresent(fields, 'currency', plan.currency);
  return fields;
}

/**
 * POST onboarding/broker/accept — invitation-based broker onboarding (userId, no token).
 */
export function buildBrokerAcceptPayload(registration, options = {}) {
  const { account = {}, organisation = {}, plan = {}, invitation = {} } = registration;
  const userId =
    resolveOnboardingUserId(
      { userId: options.userId, invitationId: options.invitationId },
      registration,
    ) || trim(invitation.userId || invitation.invitationId);

  const body = {
    ...buildAccountFields(account),
    asBroker: true,
    id: userId,
    ...buildOrganisationFields(organisation, options),
    ...buildPlanFields(plan),
  };

  if (!body.id) {
    delete body.id;
  }

  return body;
}

function buildOrgUserOrganisationFields(organisation = {}) {
  const fields = {};
  setIfPresent(fields, 'fullBusinessName', organisation.businessName);
  setIfPresent(fields, 'tradingName', organisation.tradingName);
  setIfPresent(fields, 'registrationNumber', organisation.registrationNumber);
  return fields;
}

function resolveOrgUserAcceptId(registration, options = {}) {
  return resolveOnboardingUserId(
    { userId: options.userId, invitationId: options.invitationId || options.id },
    registration,
  );
}

/**
 * POST onboarding/broker/promocode-subscription — broker subscription key or promo onboarding.
 */
export function buildBrokerPromocodePayload(registration, options = {}) {
  const { account = {}, organisation = {}, plan = {}, activation = {} } = registration;
  const body = {
    ...buildAccountFields(account),
    asBroker: resolveAsBroker(registration, options),
    ...buildOrganisationFields(organisation, options),
    ...buildPlanFields(plan),
  };

  setIfPresent(body, 'subscriptionKey', activation.subscriptionKey);
  setIfPresent(body, 'promoCode', activation.promoCode);

  return body;
}

/**
 * POST onboarding/orguser/accept — org / end-user onboarding (invitation, subscription key, or promo).
 */
export function buildOrgUserAcceptPayload(registration, options = {}) {
  const { account = {}, organisation = {}, plan = {}, activation = {} } = registration;
  const body = {
    ...buildAccountFields(account),
    ...buildOrgUserOrganisationFields(organisation),
    ...buildPlanFields(plan),
  };

  const inviteId = resolveOrgUserAcceptId(registration, options);
  if (inviteId) {
    body.id = inviteId;
  }

  setIfPresent(body, 'subscriptionKey', activation.subscriptionKey);
  setIfPresent(body, 'promoCode', activation.promoCode);

  return body;
}

/**
 * @deprecated Use buildBrokerAcceptPayload or buildOrgUserAcceptPayload.
 */
export default function buildOnboardingRegisterPayload(registration, options = {}) {
  const userType = resolveUserType(registration, options);
  const hasInvitation = Boolean(resolveOnboardingUserId({}, registration));

  if (userType === ONBOARDING_USER_TYPE.BROKER && hasInvitation) {
    return buildBrokerAcceptPayload(registration, options);
  }

  return buildOrgUserAcceptPayload(registration, options);
}
